<template>
  <div v-if="content" class="prompt-input-container">
    <SelectedFiltersComponent />
    <div class="title-container">
      <BbBtn size="x-small" type="icon" icon="arrowleft" icon-size="small" no-background @click="goBack" />
      <TextBody tag="span" :text="title" :body-type="2" weight="medium" />
    </div>
    <TipTapEditor v-model="content" :dataContent="dataContent" />
    <BbBtn
      size="x-small"
      color="#6222e3"
      :is-disabled="isDisabled"
      @click="handleSubmit"
      :width="36"
      :height="36"
      class="submitButton"
    >
      <BbIcon size="small" name="arrow-up" />
    </BbBtn>
  </div>
</template>

<script>
import { computed, onUnmounted, ref, watch } from 'vue';

import TipTapEditor from '../../tiptap/index.vue';
import useConversations from '~/composables/useConversations';
import useQuickPrompts from '~/composables/useQuickPrompts';
import TextBody from '~/components/common/TextBody.vue';
import {
  extractVariablesFromHTML,
  getRequiredFieldKeys,
  hasEmptyRequiredFields,
} from '~/components/tiptap/utils/datasetHelpers';
import SelectedFiltersComponent from '@/services/filter-form-service/components/SelectedFiltersComponent.vue';

export default {
  props: {
    filtersConfig: {
      type: Array,
      default: () => [],
    },
    externalDisabled: {
      type: Boolean,
      default: false,
    },
  },
  components: { SelectedFiltersComponent, TextBody, TipTapEditor },
  emits: ['onSubmit'],
  setup(props, { emit }) {
    const { isTyping } = useConversations();
    const {
      defaultPromptFields,
      currentFields,
      setCurrentFields,
      setInitialCardsState,
      setDisplayPromptsInNewTab,
      currentPromptCard,
      setCurrentPromptCard,
      completeFilterValuesForProductAndCountry,
      completeOptionsAndFiltersForProductAndCountry,
      handleTemplateVariables,
    } = useQuickPrompts();

    const title = ref('');
    const content = ref('');
    const dataContent = ref([]);
    // Prevents the content watcher and currentFields watcher from overwriting
    // dataContent while we are programmatically resetting the card template.
    let isProgrammaticReset = false;

    const isDisabled = computed(() => {
      if (props.externalDisabled) return true;

      if (isTyping.value) {
        return true;
      }

      const variables = extractVariablesFromHTML(content.value);
      const requiredKeys = getRequiredFieldKeys(dataContent.value);

      return hasEmptyRequiredFields(variables, requiredKeys);
    });

    const handleSubmit = () => {
      if (!isDisabled.value && content.value?.trim() !== '') {
        emit('onSubmit');
        isProgrammaticReset = true;
        content.value = currentPromptCard.value?.template;
        isProgrammaticReset = false;
      }
    };

    const goBack = () => {
      setInitialCardsState();
      setDisplayPromptsInNewTab(true);
      setCurrentPromptCard(null);
    };

    onUnmounted(() => {
      const prompt = currentPromptCard.value;
      setInitialCardsState();
      setCurrentPromptCard(prompt); // we'll need this to come back to a prompt when changing tabs without choosing a new prompt
    });

    watch(
      () => content.value,
      (newValue) => {
        if (isProgrammaticReset) return;
        handleTemplateVariables(newValue);
      },
    );

    watch(
      currentPromptCard,
      async (newCard) => {
        isProgrammaticReset = true;
        title.value = newCard.title;
        content.value = newCard.template;
        dataContent.value = newCard.data;
        // in case we come back to the prompt after setting filters, we need to fill the fields with current values
        if (defaultPromptFields.value?.length) {
          dataContent.value = dataContent.value.map((field) => {
            const existing = defaultPromptFields.value.find((f) => f.variable === field.key || f.key === field.key);
            return {
              ...field,
              value: existing ? existing.value || '' : field.value || '',
            };
          });
          dataContent.value = await completeFilterValuesForProductAndCountry(dataContent.value);
        }
        // Sync the filled values to Vuex so handleSubmit sends the correct values.
        const fieldsToSync = dataContent.value.map((f) => {
          let resolvedValue = f.value || '';
          let label = '';
          if (f.options?.length && resolvedValue) {
            // Build lookup maps keyed by lowercase for case-insensitive matching
            const byValueKey = {};
            const valueToLabelMap = {};
            const labelToValueMap = {};
            (f.options || []).forEach((opt) => {
              const v = typeof opt === 'string' ? opt : opt.value || '';
              const l = typeof opt === 'string' ? opt : opt.label || opt.value || '';
              byValueKey[v.toLowerCase()] = v;
              valueToLabelMap[v.toLowerCase()] = l;
              labelToValueMap[l.toLowerCase()] = v;
            });
            const lower = resolvedValue.toLowerCase();
            // Resolve: label→value if needed
            resolvedValue = byValueKey[lower] ?? labelToValueMap[lower] ?? resolvedValue;
            label = valueToLabelMap[resolvedValue.toLowerCase()] || '';
          }
          return { variable: f.key, value: resolvedValue, label };
        });
        if (fieldsToSync.length) {
          setCurrentFields(fieldsToSync);
        }
        isProgrammaticReset = false;
      },
      { deep: true, immediate: true },
    );

    watch(
      () => currentFields.value,
      (newCurrentFields) => {
        if (isProgrammaticReset) return;
        dataContent.value = dataContent.value.map((field) => {
          const existing = newCurrentFields.find((f) => f.variable === field.key || f.key === field.key);
          return {
            ...field,
            value: existing ? existing.value || '' : field.value || '',
          };
        });
      },
      { deep: true, immediate: true },
    );

    watch(
      () => props.filtersConfig,
      async (newFiltersConfig) => {
        if (!newFiltersConfig?.length || !dataContent.value?.length) {
          return;
        }

        dataContent.value = await completeOptionsAndFiltersForProductAndCountry({
          variables: dataContent.value,
          filtersConfig: newFiltersConfig,
        });
      },
      { deep: true, immediate: true },
    );

    return { content, dataContent, isDisabled, handleSubmit, title, goBack };
  },
};
</script>

<style lang="scss" scoped>
.prompt-input-container {
  position: relative;
  border-radius: 24px;
  border: 1px solid $bb-neutral-4;
  padding: $bb-spacing-6 $bb-spacing-10 $bb-spacing-6 $bb-spacing-6;
  margin: $bb-spacing-3 $bb-spacing-4;
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;

  .title-container {
    margin-bottom: $bb-spacing-4;
    display: flex;
    align-items: center;
    gap: $bb-spacing-2;

    :deep(.font-weight-medium) {
      font-weight: 600 !important;
    }

    :deep(.v-btn__content) {
      i {
        color: $bb-primary-3;
      }
    }
  }

  .submitButton {
    position: absolute;
    right: $bb-spacing-4;
    bottom: $bb-spacing-4;
  }
}

:deep(.tiptap) {
  p:not(:last-of-type) {
    margin-bottom: $bb-spacing-6;
  }

  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  .variable-select {
    border-bottom: 1px dashed $bb-primary-3;
    display: inline-block;
    position: relative;
  }

  .variable-select-dropdown {
    position: absolute;
    width: fit-content;
    min-width: 100%;
    top: 100%;
    left: 0;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin-top: 2px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 100;
    display: none;
    transition: all 0.2s ease;
    overflow-y: auto;
  }

  .variable-select-dropdown::before,
  .variable-select-dropdown::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-size: 12px;
    color: #555;
    z-index: 10;
    display: none;
  }

  .variable-select-dropdown.scroll-bottom::after {
    content: '▼'; /* down arrow */
    display: block;
    bottom: 2px;
    font-size: 8px;
  }

  .variable-select-dropdown-option {
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: $bb-neutral-6;
    }
  }

  .variable-select-control {
    padding: 0 12px;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    min-width: fit-content;
    transition: all 0.2s ease;
  }

  .variable-select.selected {
    border-bottom: 1px solid $bb-primary-3;
    background: rgba(98, 34, 227, 0.05);

    .variable-select-control {
      color: $bb-primary-3 !important;
    }
  }

  .variable-input.selected {
    background: rgba(98, 34, 227, 0.05);
  }

  .variable-select select {
    outline: none;
    color: $bb-neutral-1;
    text-align: center;
  }

  .variable-select select::placeholder,
  .variable-select-control.placeholder-active {
    color: $bb-neutral-2;
  }

  .variable-input span:focus,
  .variable-select div:focus {
    color: $bb-primary-3 !important;
  }

  .variable-input span {
    text-decoration-color: $bb-primary-3 !important;
  }

  .variable-editable {
    display: inline-block;
    min-width: 50px;
    max-width: 100%;
    white-space: pre-wrap;
    word-break: break-word;
    outline: none;
    padding: 2px 4px;
    box-sizing: border-box;
    line-height: 1.4em;
    color: $bb-neutral-1;
    text-decoration: underline;
    text-decoration-style: dashed;
    text-decoration-color: $bb-primary-3;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }

  .variable-input-measure {
    visibility: hidden;
    position: absolute;
    white-space: pre;
  }
}
</style>
